"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { X, Minus, Plus, ShoppingCart } from "lucide-react";
import { useCart } from "@/store/cart";
import { cn, formatPrice } from "@/lib/utils";
import { parseStringProduct } from "@/lib/catalog/shopify-catalog";
import {
  formatGaugeRange,
  formatGaugePipeList,
  stringCountLabel,
  categoryFromInstrumentName,
} from "@/lib/catalog/gauge-format";
import { GaugeInfo } from "@/components/common/GaugeInfo";
import { chipStyle } from "@/components/configurator/chip-style";
import {
  findVariantForSelection,
  getDefaultPurchasableVariant,
  getDisabledOptionValues,
  type SelectedOptions,
} from "@/lib/products/variant-options";
import { ColorSwatch } from "./ColorSwatch";
import { ProductImagePlaceholder } from "./ProductImagePlaceholder";
import type { ShopifyProduct } from "@/types/shopify";
import { CART_LINE_QUANTITY_MAX } from "@/lib/cart/limits";

interface QuickAddModalProps {
  product: ShopifyProduct;
  open: boolean;
  onClose: () => void;
}

export function QuickAddModal({ product, open, onClose }: QuickAddModalProps) {
  const { addItem } = useCart();
  const panelRef = useRef<HTMLDivElement>(null);
  // Captured before focus moves into the dialog, so closing can hand focus back
  // to the control that opened it rather than to <body>.
  const openerRef = useRef<HTMLElement | null>(null);

  const colorOption = product.options.find((o) => o.name.toLowerCase() === "color");
  const hasColors = (colorOption?.values.length ?? 0) > 1;

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset selection each time the modal opens.
  useEffect(() => {
    if (open) {
      setSelectedColor(null);
      setQuantity(1);
      setAdding(false);
      setError(null);
      openerRef.current = document.activeElement as HTMLElement | null;
      panelRef.current?.focus();
    }
  }, [open]);

  // Escape closes; Tab stays inside the dialog; focus returns to whatever
  // opened it. Previously the dialog had the ARIA attributes but none of the
  // keyboard behaviour, so Tab walked off into the page behind it.
  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [
        ...panel.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      openerRef.current?.focus?.();
    };
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const meta = parseStringProduct(product);
  const range = formatGaugeRange(meta.individualGauges);
  const gaugeList = meta.individualGauges
    ? formatGaugePipeList(meta.individualGauges, categoryFromInstrumentName(meta.instrument))
    : "";
  const variants = product.variants.nodes;

  // The variant that will actually be added. When the product has no color
  // choice, fall back to the default purchasable variant.
  const selectedOptions: SelectedOptions = selectedColor ? { Color: selectedColor } : {};
  const activeVariant = hasColors
    ? selectedColor
      ? findVariantForSelection(variants, {}, "Color", selectedColor)
      : undefined
    : getDefaultPurchasableVariant(variants);

  // Image: the packaging box (featured image) until a color is chosen, then
  // the selected color's string photo.
  const image = activeVariant?.image ?? product.images.nodes[0] ?? null;

  const unitPrice = activeVariant?.price ?? product.priceRange.minVariantPrice;
  const lineTotal = (parseFloat(unitPrice.amount) * quantity).toFixed(2);

  const colorRequiredUnmet = hasColors && !selectedColor;
  const canAdd =
    !!activeVariant && activeVariant.availableForSale && !colorRequiredUnmet && !adding;

  async function handleAdd() {
    // `adding` also guards against a double submit from a fast second click.
    if (!activeVariant || !canAdd) return;
    setAdding(true);
    setError(null);
    try {
      await addItem(activeVariant.id, quantity);
      onClose();
    } catch {
      // This used to fail silently: the modal stayed open, the cart was
      // unchanged, and nothing told the customer why.
      setError("We couldn't add that to your cart. Please try again.");
    } finally {
      setAdding(false);
    }
  }

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-black/60"
        aria-hidden="true"
      />

      {/* Panel: bottom sheet on mobile, centered modal on desktop */}
      <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center pointer-events-none">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Quick add ${product.title}`}
          tabIndex={-1}
          className={cn(
            "pointer-events-auto w-full sm:max-w-md flex flex-col shadow-2xl outline-none",
            "max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-xl"
          )}
          style={{ backgroundColor: "#141414", border: "1px solid #2A2A2A" }}
        >
          {/* Header */}
          <div
            className="flex items-start justify-between gap-3 px-5 py-4 border-b sticky top-0 z-10"
            style={{ borderColor: "#2A2A2A", backgroundColor: "#141414" }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="relative w-14 h-14 rounded-md overflow-hidden flex-shrink-0"
                style={{ backgroundColor: "#0D0D0D" }}
              >
                {image ? (
                  <Image
                    src={image.url}
                    alt={image.altText ?? product.title}
                    fill
                    className="object-cover"
                    sizes="56px"
                  />
                ) : (
                  <ProductImagePlaceholder />
                )}
              </div>
              <div className="min-w-0">
                <h2
                  className="text-sm font-semibold leading-snug truncate"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {product.productType || product.title}
                </h2>
                {(meta.gaugeName || meta.stringCount > 0) && (
                  <p className="text-xs mt-0.5" style={{ color: "#9A9A9A" }}>
                    {meta.gaugeName && `${meta.gaugeName} Gauge`}
                    {range ? ` · ${range}` : ""}
                    {meta.stringCount > 0 ? ` · ${stringCountLabel(meta.stringCount)}` : ""}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md transition-colors hover:bg-[#1E1E1E] flex-shrink-0"
              style={{ color: "#9A9A9A" }}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 flex flex-col gap-5">
            {/* Full gauge breakdown — wound strings marked with w */}
            {gaugeList && (
              <p className="text-xs font-mono flex items-center gap-1.5 flex-wrap" style={{ color: "#9A9A9A" }}>
                <span>Gauges: {gaugeList}</span>
                <GaugeInfo />
              </p>
            )}

            {/* Color selector */}
            {hasColors && (
              <div>
                <p className="text-sm font-semibold mb-2">
                  Color:{" "}
                  <span style={{ color: "#9A9A9A" }}>{selectedColor ?? "Select"}</span>
                </p>
                <div className="flex flex-col gap-2">
                  {(colorOption?.values ?? []).map((val) => {
                    const disabled =
                      getDisabledOptionValues(variants, selectedOptions, "Color", [val]).length > 0;
                    const isSelected = selectedColor === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => !disabled && setSelectedColor(val)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-sm border text-left transition-all",
                          "disabled:opacity-35 disabled:cursor-not-allowed"
                        )}
                        style={{
                          ...chipStyle(isSelected),
                          backgroundColor: isSelected ? "#E8C54715" : "#0D0D0D",
                        }}
                      >
                        <ColorSwatch name={val} hexCode={null} size="md" />
                        <span
                          className="text-sm"
                          style={{ color: isSelected ? "#E8C547" : "#E0E0E0" }}
                        >
                          {val}
                        </span>
                        {disabled && (
                          <span className="ml-auto text-xs" style={{ color: "#8A8A8A" }}>
                            Out of stock
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <p className="text-sm font-semibold mb-2">Quantity</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="p-2.5 rounded-sm border transition-colors hover:bg-[#1E1E1E] disabled:opacity-35"
                  style={{ borderColor: "#2A2A2A", color: "#9A9A9A" }}
                  aria-label="Decrease quantity"
                >
                  <Minus size={16} />
                </button>
                <span className="text-base font-semibold w-8 text-center" aria-live="polite">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(CART_LINE_QUANTITY_MAX, q + 1))}
                  disabled={quantity >= CART_LINE_QUANTITY_MAX}
                  className="p-2.5 rounded-sm border transition-colors hover:bg-[#1E1E1E] disabled:opacity-35"
                  style={{ borderColor: "#2A2A2A", color: "#9A9A9A" }}
                  aria-label="Increase quantity"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Footer / CTA */}
          <div
            className="px-5 py-4 border-t sticky bottom-0"
            style={{ borderColor: "#2A2A2A", backgroundColor: "#141414" }}
          >
            {colorRequiredUnmet && (
              <p className="text-xs mb-2" style={{ color: "#9A9A9A" }}>
                Choose a color to continue.
              </p>
            )}
            {error && (
              <p className="text-xs mb-2" role="alert" style={{ color: "#E84040" }}>
                {error}
              </p>
            )}
            <button
              onClick={handleAdd}
              disabled={!canAdd}
              className="w-full flex items-center justify-center gap-2 py-3.5 text-sm font-bold rounded-sm transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
            >
              {adding ? (
                <span className="w-4 h-4 border-2 border-[#0A0A0A30] border-t-[#0A0A0A] rounded-full animate-spin" />
              ) : (
                <ShoppingCart size={16} />
              )}
              {adding ? "Adding…" : `Add to Cart · ${formatPrice(lineTotal)}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
